import React, { useEffect, useRef, useState } from "react";

const agents = {
    bookings: {
        name: "Bookings",
        subtitle: "Room Reservations",
        initials: "B",
        message: [
            "Welcome to Znorm Homes!",
            "How can we help you?",
        ],
        phone: "918431276142",
        defaultText: "Hi, I have a booking enquiry.",
    },
    support: {
        name: "Support",
        subtitle: "Customer Service",
        initials: "S",
        message: [
            "Hi there!",
            "How can we help you with your stay?",
        ],
        phone: "918431276142",
        defaultText: "Hi, I need support regarding my stay.",
    },
};

const WhatsAppIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
);

const WhatsAppWidget = () => {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState("initial");
    const [isTyping, setIsTyping] = useState(false);
    const widgetRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                open &&
                widgetRef.current &&
                !widgetRef.current.contains(event.target)
            ) {
                setOpen(false);
                setTimeout(() => setStep("initial"), 300);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (open) {
            setIsTyping(true);
            const timer = setTimeout(() => {
                setIsTyping(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [open, step]);

    const agent = step !== "initial" ? agents[step] : null;

    const handleClose = () => {
        setOpen(false);
        setTimeout(() => setStep("initial"), 300);
    };

    const handleSelect = (role) => {
        setStep(role);
    };

    return (
        <div
            ref={widgetRef}
            className="pointer-events-none fixed bottom-6 right-6 z-[9999] flex flex-col items-end gap-4"
        >
            {/* Popup */}
            <div
                className={`origin-bottom-right transition-all duration-300 ${open ? "pointer-events-auto scale-100 opacity-100" : "pointer-events-none scale-90 opacity-0"}`}
            >
                <div
                    className="w-[356px] max-w-[90vw] overflow-hidden rounded-2xl shadow-2xl"
                    style={{ maxHeight: "calc(100vh - 120px)" }}
                >
                    {/* Header */}
                    <div className="relative flex items-center gap-3 bg-[#095E54] px-6 py-4">
                        <div className="flex h-[50px] w-[50px] flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white">
                            <img
                                src="/favicon.png"
                                alt="Znorm Homes"
                                className="h-full w-full object-cover scale-99"
                            />
                        </div>
                        <div className="flex flex-col">
                            <span className="font-inter text-[16px] font-semibold leading-[19px] text-white">
                                {agent ? agent.name : "Znorm Homes"}
                            </span>
                            <span className="font-inter text-[12px] font-normal leading-[15px] text-white">
                                {agent ? agent.subtitle : "Sales Support"}
                            </span>
                        </div>
                        <button
                            onClick={handleClose}
                            className="absolute right-3 top-3 flex h-[24px] w-[24px] items-center justify-center rounded-full p-1 transition-colors hover:bg-black/10 text-white"
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                            </svg>
                        </button>
                    </div>

                    {/* Chat body */}
                    <div
                        className="relative flex min-h-[230px] flex-col justify-between p-[18px]"
                        style={{
                            backgroundColor: "#ECE5DD",
                            backgroundImage:
                                "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4cfc6' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
                        }}
                    >
                        {/* Message bubble */}
                        <div className="mb-4 drop-shadow-[0_0_4px_rgba(0,0,0,0.16)]">
                            <div className="relative ml-[10px] inline-block">
                                {/* Tail */}
                                <div
                                    className="absolute -left-[10px] top-0 h-0 w-0"
                                    style={{
                                        borderTop: "10px solid white",
                                        borderLeft: "10px solid transparent",
                                    }}
                                />
                                <div className="rounded-[0_15px_15px_15px] bg-white px-4 py-3">
                                    {isTyping ? (
                                        <div className="flex h-[18px] items-center gap-1 px-1">
                                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]"></span>
                                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]"></span>
                                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400"></span>
                                        </div>
                                    ) : step === "initial" ? (
                                        <>
                                            <p className="font-outfit text-[13px] leading-[18px] text-black">
                                                Hello there!
                                            </p>
                                            <p className="font-outfit text-[13px] leading-[18px] text-black">
                                                Please choose your profile.
                                            </p>
                                        </>
                                    ) : (
                                        agent?.message.map((line, i) => (
                                            <p
                                                key={i}
                                                className="font-outfit text-[13px] leading-[18px] text-black"
                                            >
                                                {line}
                                            </p>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div
                            className={`flex flex-col gap-2 transition-all duration-300 ${isTyping
                                ? "pointer-events-none translate-y-2 opacity-0"
                                : "translate-y-0 opacity-100"
                                }`}
                        >
                            {step === "initial" ? (
                                <>
                                    <button
                                        onClick={() =>
                                            handleSelect("bookings")
                                        }
                                        className="flex h-[40px] w-full items-center justify-between rounded-[18px] bg-white px-3 shadow-sm transition-shadow hover:shadow-md"
                                    >
                                        <span className="rounded-[12px] border border-white bg-white px-[10px] py-[6px] font-outfit text-[14px] leading-[17px] text-black">
                                            Bookings
                                        </span>
                                        <WhatsAppIcon className="h-[18px] w-[18px] text-[#25D366]" />
                                    </button>
                                    <button
                                        onClick={() =>
                                            handleSelect("support")
                                        }
                                        className="flex h-[40px] w-full items-center justify-between rounded-[18px] bg-white px-3 shadow-sm transition-shadow hover:shadow-md"
                                    >
                                        <span className="rounded-[12px] border border-white bg-white px-[10px] py-[6px] font-outfit text-[14px] leading-[17px] text-black">
                                            Support
                                        </span>
                                        <WhatsAppIcon className="h-[18px] w-[18px] text-[#25D366]" />
                                    </button>
                                </>
                            ) : (
                                <a
                                    href={`https://api.whatsapp.com/send?phone=${agent.phone}&text=${encodeURIComponent(agent.defaultText)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex h-[40px] w-full items-center justify-center gap-1 rounded-[18px] bg-[#14C656] transition-colors hover:bg-[#12b34e]"
                                >
                                    <WhatsAppIcon className="h-[18px] w-[18px] text-white" />
                                    <span className="font-outfit text-[13px] leading-[14px] text-white">
                                        Start Chat
                                    </span>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating WhatsApp button with glow */}
            <button
                onClick={() => setOpen(!open)}
                className="group pointer-events-auto relative flex h-[58px] w-[58px] items-center justify-center rounded-full"
                aria-label="Chat on WhatsApp"
            >
                {/* Outer glow ring */}
                <span className="absolute inset-[-8px] rounded-full border-[1.5px] border-[#7AD06D]/30 transition-all duration-500 group-hover:border-[#7AD06D]/50" />
                {/* Inner glow ring */}
                <span className="absolute inset-[-3px] rounded-full border-[1.5px] border-[#7AD06D]/20 transition-all duration-500 group-hover:border-[#7AD06D]/40" />
                {/* Pulsing glow */}
                <span
                    className="absolute inset-0 animate-ping rounded-full bg-[#25D366]/20"
                    style={{ animationDuration: "2s" }}
                />
                {/* Button background */}
                <span className="absolute inset-0 rounded-full bg-white shadow-[0_2px_12px_rgba(37,211,102,0.3)]" />
                {/* Icon */}
                <WhatsAppIcon className="relative z-10 h-[28px] w-[28px] text-[#25D366]" />
                {/* Red notification dot */}
                <span className="absolute right-[2px] top-[2px] z-10 h-[12px] w-[12px] rounded-full border-2 border-white bg-red-500" />
            </button>
        </div>
    );
};

export default WhatsAppWidget;
